import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronRight,
  FileText,
  Calendar,
  Crown,
  Star,
  RefreshCw,
  Headphones,
  ArrowUp,
  Eye,
  ClipboardList,
  Download,
  ExternalLink,
  Image,
  File,
  MessageCircle,
  Send,
  Loader2,
  Video,
  Link2,
  X,
  CalendarPlus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import DashboardLayout from "../../components/DashboardLayout";
import CaseNotesSection from "../../components/cases/CaseNotesSection";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advancing, setAdvancing] = useState(false);
  const [completingStep, setCompletingStep] = useState(null);
  const [settingResult, setSettingResult] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [ds160Data, setDs160Data] = useState({ confirmation_number: "", completed: false });
  const [invitingSession, setInvitingSession] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  // Meeting/Calendar state
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    date: "",
    time: "",
    zoom_link: "",
    subject: "",
    appointment_type: "videollamada"
  });

  useEffect(() => {
    fetchCase();
    fetchDocuments();
  }, [caseId]);

  // Load chat messages and meetings when caseData is available
  useEffect(() => {
    if (caseData?.user_id) {
      fetchChatMessages(caseData.user_id);
    }
    if (caseData?.client_id) {
      fetchMeetings(caseData.client_id);
    }
  }, [caseData?.user_id, caseData?.client_id]);

  const fetchCase = async () => {
    try {
      setError(null);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCaseData(res.data);
      if (res.data?.ds160_data) {
        setDs160Data(res.data.ds160_data);
      }
    } catch (error) {
      console.error(error);
      setError(error.response?.data?.detail || "Error al cargar el caso");
      toast.error("Error al cargar el caso");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/documents?case_id=${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both array response and object with documents key
      const docs = Array.isArray(res.data) ? res.data : (res.data?.documents || []);
      setDocuments(docs);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchChatMessages = async (userId) => {
    if (!userId) return;
    setLoadingChat(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/support-chat/admin/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error fetching chat:", error);
    } finally {
      setLoadingChat(false);
    }
  };

  const sendChatMessage = async () => {
    if (!newChatMessage.trim() || sendingChat || !caseData?.user_id) return;
    
    setSendingChat(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/support-chat/admin/send/${caseData.user_id}`,
        { content: newChatMessage.trim(), case_id: caseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewChatMessage("");
      fetchChatMessages(caseData.user_id);
      toast.success("Mensaje enviado");
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingChat(false);
    }
  };

  const fetchMeetings = async (clientId) => {
    if (!clientId) return;
    setLoadingMeetings(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/calendar/appointments?client_id=${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(res.data.appointments || []);
    } catch (error) {
      console.error("Error fetching meetings:", error);
    } finally {
      setLoadingMeetings(false);
    }
  };

  const createMeeting = async () => {
    if (!meetingForm.date || !meetingForm.time || !meetingForm.subject) {
      toast.error("Completa los campos requeridos");
      return;
    }
    
    setCreatingMeeting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/calendar/appointments`,
        {
          client_id: caseData?.client_id || caseData?.user_id,
          case_id: caseId,
          date: meetingForm.date,
          time: meetingForm.time,
          zoom_link: meetingForm.zoom_link,
          subject: meetingForm.subject,
          appointment_type: meetingForm.appointment_type,
          duration_minutes: 60
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cita agendada exitosamente");
      setShowMeetingModal(false);
      setMeetingForm({ date: "", time: "", zoom_link: "", subject: "", appointment_type: "videollamada" });
      fetchMeetings(caseData?.client_id || caseData?.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agendar cita");
    } finally {
      setCreatingMeeting(false);
    }
  };

  const cancelMeeting = async (appointmentId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/calendar/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cita cancelada");
      fetchMeetings(caseData?.client_id || caseData?.user_id);
    } catch (error) {
      toast.error("Error al cancelar cita");
    }
  };

  const completeSpecificStep = async (stepOrder) => {
    setCompletingStep(stepOrder);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/cases/${caseId}/complete-step/${stepOrder}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Paso ${stepOrder} completado exitosamente`);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al completar el paso");
    } finally {
      setCompletingStep(null);
    }
  };

  const getDocumentIcon = (docType) => {
    if (docType?.includes("image") || docType?.includes("photo") || docType?.includes("passport")) {
      return <Image className="w-5 h-5 text-blue-600" />;
    }
    return <File className="w-5 h-5 text-navy-600" />;
  };

  const handleInviteLiveSession = async () => {
    if (!caseData?.user_id) {
      toast.error("No se puede identificar al cliente");
      return;
    }
    
    setInvitingSession(true);
    try {
      // Use direct access endpoint - no client permission needed
      const response = await axios.post(`${API}/live-session/start-direct`, {
        client_id: caseData.user_id,
        message: `Supervisando caso de ${caseData.user?.name || 'cliente'}`
      });
      
      if (response.data.success) {
        toast.success(`Sesión iniciada para ${caseData.user?.name || 'cliente'}`);
        // Navigate directly to live session viewer
        navigate(`/admin/live-sessions?session=${response.data.session_id}`);
      }
    } catch (error) {
      console.error('Error starting direct session:', error);
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setInvitingSession(false);
    }
  };

  const advanceStep = async () => {
    setAdvancing(true);
    try {
      await axios.post(`${API}/admin/cases/${caseId}/advance-step`);
      toast.success("Paso avanzado exitosamente");
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al avanzar paso");
    } finally {
      setAdvancing(false);
    }
  };

  const updateDS160 = async () => {
    try {
      await axios.post(`${API}/cases/${caseId}/ds160`, ds160Data);
      toast.success("DS-160 actualizado");
      fetchCase();
    } catch (error) {
      toast.error("Error al actualizar DS-160");
    }
  };

  const setFinalResult = async (result) => {
    setSettingResult(true);
    try {
      await axios.post(`${API}/cases/${caseId}/result`, { result });
      toast.success(`Resultado establecido: ${result}`);
      fetchCase();
    } catch (error) {
      toast.error("Error al establecer resultado");
    } finally {
      setSettingResult(false);
    }
  };

  const updateUserPlan = async (newPlan) => {
    setUpdatingPlan(true);
    try {
      await axios.post(`${API}/admin/users/${caseData.user?.id}/plan`, { plan_type: newPlan });
      const planNames = { estandar: "Estándar", premium: "Premium", vip: "VIP Enterprise" };
      toast.success(`Plan actualizado a ${planNames[newPlan] || newPlan}`);
      fetchCase();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar plan");
    } finally {
      setUpdatingPlan(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !caseData) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-navy-900 mb-2">
            {error ? "Error al cargar caso" : "Caso no encontrado"}
          </h2>
          <p className="text-navy-500 mb-6">
            {error || "No se pudo cargar la información del caso."}
          </p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => navigate("/admin/clientes")}
              className="bg-navy-600 hover:bg-navy-700 text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Clientes
            </Button>
            <Button 
              onClick={() => {
                setLoading(true);
                setError(null);
                fetchCase();
              }}
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate("/admin/cases")}
            className="mb-4 text-navy-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a casos
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
                Caso de {caseData.user?.name}
              </h1>
              <p className="text-navy-600">ID: {caseData.id.slice(0, 8)}...</p>
            </div>
            <div className="flex gap-2">
              {/* Botón Ver Pantalla del Cliente - Acceso Directo */}
              <Button
                onClick={handleInviteLiveSession}
                variant="outline"
                className="border-green-200 text-green-700 hover:bg-green-50"
                disabled={invitingSession}
                data-testid="start-live-session-btn"
              >
                {invitingSession ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Headphones className="w-4 h-4 mr-2" />
                )}
                {invitingSession ? "Conectando..." : "Ver Pantalla"}
              </Button>
              
              {caseData.current_step < 6 && caseData.status === "active" && (
                <Button
                  onClick={advanceStep}
                  disabled={advancing}
                  className="bg-gold-600 hover:bg-gold-700 text-white"
                  data-testid="advance-step-btn"
                >
                  {advancing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Avanzar al siguiente paso
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Client Info & Progress */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Client Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-elevated p-6"
          >
            <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
              Información del Cliente
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gold-600" />
                </div>
                <div>
                  <p className="text-sm text-navy-500">Nombre</p>
                  <p className="font-medium text-navy-900">{caseData.user?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-navy-500">Email</p>
                  <p className="font-medium text-navy-900">{caseData.user?.email}</p>
                </div>
              </div>
              {caseData.user?.phone && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Teléfono</p>
                    <p className="font-medium text-navy-900">{caseData.user.phone}</p>
                  </div>
                </div>
              )}
              
              {/* User Plan Management */}
              <div className="pt-4 border-t border-navy-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    caseData.user?.plan_type === "vip" ? "bg-amber-100" : 
                    caseData.user?.plan_type === "premium" ? "bg-blue-100" : "bg-gray-100"
                  }`}>
                    {caseData.user?.plan_type === "vip" ? (
                      <Crown className="w-5 h-5 text-amber-600" />
                    ) : caseData.user?.plan_type === "premium" ? (
                      <Star className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Star className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-navy-500">Plan Actual</p>
                    <p className={`font-medium ${
                      caseData.user?.plan_type === "vip" ? "text-amber-700" : 
                      caseData.user?.plan_type === "premium" ? "text-blue-700" : "text-gray-700"
                    }`}>
                      {caseData.user?.plan_type === "vip" ? "Plan VIP" : 
                       caseData.user?.plan_type === "premium" ? "Plan Premium" : "Plan Estándar"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={caseData.user?.plan_type === "estandar" ? "default" : "outline"}
                    className={caseData.user?.plan_type === "estandar" 
                      ? "flex-1 bg-gray-600 hover:bg-gray-700 text-white" 
                      : "flex-1"
                    }
                    onClick={() => updateUserPlan("estandar")}
                    disabled={updatingPlan || caseData.user?.plan_type === "estandar"}
                    data-testid="set-estandar-plan-btn"
                  >
                    Estándar
                  </Button>
                  <Button
                    size="sm"
                    variant={caseData.user?.plan_type === "premium" ? "default" : "outline"}
                    className={caseData.user?.plan_type === "premium" 
                      ? "flex-1 bg-blue-600 hover:bg-blue-700 text-white" 
                      : "flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                    }
                    onClick={() => updateUserPlan("premium")}
                    disabled={updatingPlan || caseData.user?.plan_type === "premium"}
                    data-testid="set-premium-plan-btn"
                  >
                    <Star className="w-3 h-3 mr-1" />
                    Premium
                  </Button>
                  <Button
                    size="sm"
                    variant={caseData.user?.plan_type === "vip" ? "default" : "outline"}
                    className={caseData.user?.plan_type === "vip" 
                      ? "flex-1 bg-amber-500 hover:bg-amber-600 text-white" 
                      : "flex-1 border-amber-300 text-amber-600 hover:bg-amber-50"
                    }
                    onClick={() => updateUserPlan("vip")}
                    disabled={updatingPlan || caseData.user?.plan_type === "vip"}
                    data-testid="set-vip-plan-btn"
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    VIP
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Case Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-elevated p-6 lg:col-span-2"
          >
            <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
              Estado del Caso
            </h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-navy-50 rounded-lg">
                <p className="text-sm text-navy-500">Tipo de Visa</p>
                <p className="text-xl font-bold text-navy-900">{caseData.visa_type}</p>
              </div>
              <div className="p-4 bg-navy-50 rounded-lg">
                <p className="text-sm text-navy-500">Probabilidad</p>
                <p className="text-xl font-bold text-emerald-600">{caseData.probability_score}%</p>
              </div>
              <div className="p-4 bg-navy-50 rounded-lg">
                <p className="text-sm text-navy-500">Nivel de Riesgo</p>
                <p className={`text-xl font-bold ${
                  caseData.risk_level === "low" ? "text-emerald-600" :
                  caseData.risk_level === "medium" ? "text-amber-600" :
                  caseData.risk_level === "high" ? "text-red-600" : "text-navy-500"
                }`}>
                  {caseData.risk_level === "low" ? "Bajo" :
                   caseData.risk_level === "medium" ? "Medio" :
                   caseData.risk_level === "high" ? "Alto" : "Pendiente"}
                </p>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-navy-600">Progreso general</span>
                <span className="font-semibold text-navy-900">{caseData.progress_percentage}%</span>
              </div>
              <Progress value={caseData.progress_percentage} className="h-3" />
            </div>
          </motion.div>
        </div>

        {/* Steps Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-elevated p-6"
        >
          <h3 className="font-heading text-lg font-bold text-navy-900 mb-6">
            Progreso del Proceso (5 Pasos)
          </h3>
          
          <div className="space-y-4">
            {caseData.steps?.map((step, index) => (
              <div key={step.id || index} className="flex items-center gap-4 p-3 rounded-lg hover:bg-navy-50 transition-colors">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.status === "completed" ? "bg-emerald-100" :
                  step.status === "in_progress" ? "bg-gold-100" : "bg-navy-100"
                }`}>
                  {step.status === "completed" ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  ) : step.status === "in_progress" ? (
                    <Clock className="w-5 h-5 text-gold-600" />
                  ) : (
                    <span className="text-navy-500 font-semibold">{step.step_order}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${
                    step.status === "completed" ? "text-emerald-700" :
                    step.status === "in_progress" ? "text-gold-700" : "text-navy-500"
                  }`}>
                    {step.step_name}
                  </p>
                  {step.completed_at && (
                    <p className="text-sm text-navy-400">
                      Completado: {new Date(step.completed_at).toLocaleDateString('es-ES')}
                    </p>
                  )}
                </div>
                
                {/* Estado Badge */}
                <div className={`px-3 py-1 rounded-full text-sm ${
                  step.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                  step.status === "in_progress" ? "bg-gold-100 text-gold-700" : "bg-navy-100 text-navy-500"
                }`}>
                  {step.status === "completed" ? "Completado" :
                   step.status === "in_progress" ? "En progreso" : "Pendiente"}
                </div>
                
                {/* Botón Completar Paso - visible si el paso NO está completado */}
                {step.status !== "completed" && (
                  <Button
                    onClick={() => completeSpecificStep(step.step_order)}
                    disabled={completingStep === step.step_order}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`complete-step-${step.step_order}`}
                  >
                    {completingStep === step.step_order ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Completar
                      </>
                    )}
                  </Button>
                )}
              </div>
            ))}
            
            {/* Mensaje si no hay pasos */}
            {(!caseData.steps || caseData.steps.length === 0) && (
              <div className="text-center py-8 text-navy-400">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                <p>No hay pasos configurados para este caso.</p>
                <p className="text-sm">Use el botón "Reparar Pasos" en el Centro de Comando.</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Documentos del Cliente */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="card-elevated p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-navy-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Documentos del Cliente
            </h3>
            <Button
              onClick={fetchDocuments}
              variant="outline"
              size="sm"
              disabled={loadingDocs}
            >
              <RefreshCw className={`w-4 h-4 ${loadingDocs ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {loadingDocs ? (
            <div className="text-center py-6">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-navy-400" />
              <p className="text-sm text-navy-400 mt-2">Cargando documentos...</p>
            </div>
          ) : !documents || documents.length === 0 ? (
            <div className="text-center py-6 bg-navy-50 rounded-lg">
              <FileText className="w-10 h-10 mx-auto text-navy-300 mb-2" />
              <p className="text-navy-500">El cliente aún no ha subido documentos</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="flex items-center gap-4 p-3 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors"
                >
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-navy-200">
                    {getDocumentIcon(doc.document_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-navy-900 truncate">
                      {doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || doc.filename || 'Documento'}
                    </p>
                    <p className="text-sm text-navy-500">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('es-ES') : 'Sin fecha'}
                      {doc.status && (
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                          doc.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {doc.status === 'approved' ? 'Aprobado' : doc.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Ver documento */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const docUrl = doc.file_url || doc.url || doc.web_view_link || doc.cloudinary_url;
                        if (docUrl) {
                          window.open(docUrl, '_blank');
                        } else {
                          console.error('No URL found for document:', doc);
                        }
                      }}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      data-testid={`view-doc-${index}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {/* Descargar documento */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        const docId = doc.id;
                        const docName = doc.filename || doc.file_name || doc.original_filename || doc.stored_name || `documento-${index + 1}`;
                        
                        if (docId) {
                          try {
                            toast.info("Descargando archivo...");
                            const token = localStorage.getItem("token");
                            const response = await fetch(`${API}/admin/documents/${docId}/download`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (response.ok) {
                              const blob = await response.blob();
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = docName;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                              toast.success("Archivo descargado");
                              return;
                            } else {
                              throw new Error("Download failed");
                            }
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error("Error al descargar archivo");
                          }
                        } else {
                          toast.error("ID del documento no disponible");
                        }
                      }}
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      data-testid={`download-doc-${index}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {/* Resumen de documentos */}
              <div className="mt-4 pt-4 border-t border-navy-200">
                <p className="text-sm text-navy-500">
                  Total: {documents.length} documento{documents.length !== 1 ? 's' : ''} subido{documents.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Reuniones/Citas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32 }}
          className="bg-white rounded-xl border border-navy-100 overflow-hidden"
        >
          <div className="p-4 border-b border-navy-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-navy-900">Reuniones y Citas</h3>
                <p className="text-xs text-navy-500">
                  {meetings.filter(m => m.status === "scheduled").length} cita(s) programada(s)
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowMeetingModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
              size="sm"
              data-testid="schedule-meeting-btn"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Agendar Cita
            </Button>
          </div>
          
          <div className="p-4">
            {loadingMeetings ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : meetings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 text-navy-200 mx-auto mb-2" />
                <p className="text-sm text-navy-500">No hay citas programadas</p>
                <p className="text-xs text-navy-400">Agenda una cita con el cliente</p>
              </div>
            ) : (
              <div className="space-y-3">
                {meetings.slice(0, 5).map((meeting) => (
                  <div
                    key={meeting.id}
                    className={`p-3 rounded-lg border ${
                      meeting.status === "scheduled" 
                        ? "bg-blue-50 border-blue-200" 
                        : meeting.status === "completed"
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          meeting.status === "scheduled" ? "bg-blue-100" : "bg-emerald-100"
                        }`}>
                          <Video className={`w-4 h-4 ${
                            meeting.status === "scheduled" ? "text-blue-600" : "text-emerald-600"
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-navy-900 text-sm">{meeting.subject}</p>
                          <p className="text-xs text-navy-500">
                            {meeting.date} a las {meeting.time}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {meeting.zoom_link && meeting.status === "scheduled" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(meeting.zoom_link, '_blank')}
                            className="text-blue-600"
                          >
                            <Link2 className="w-4 h-4" />
                          </Button>
                        )}
                        {meeting.status === "scheduled" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => cancelMeeting(meeting.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                        {meeting.status === "completed" && (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            Completada
                          </span>
                        )}
                        {meeting.status === "cancelled" && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            Cancelada
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Chat con Cliente */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-xl border border-navy-100 overflow-hidden"
        >
          <div className="p-4 border-b border-navy-100 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-navy-900">Chat con Cliente</h3>
                <p className="text-xs text-navy-500">
                  {chatMessages.length} mensaje{chatMessages.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => caseData?.user_id && fetchChatMessages(caseData.user_id)}
              className="text-emerald-600"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Messages Area */}
          <div className="h-64 overflow-y-auto p-4 bg-navy-50">
            {loadingChat ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageCircle className="w-10 h-10 text-navy-200 mb-2" />
                <p className="text-sm text-navy-500">No hay mensajes con este cliente</p>
                <p className="text-xs text-navy-400 mt-1">Envía el primer mensaje</p>
              </div>
            ) : (
              <div className="space-y-3">
                {chatMessages.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`flex ${msg.sender_role === "admin" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-xl px-3 py-2 ${
                        msg.sender_role === "admin"
                          ? "bg-emerald-600 text-white"
                          : "bg-white text-navy-900 border border-navy-200"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.sender_role === "admin" ? "text-emerald-200" : "text-navy-400"
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Input Area */}
          <div className="p-3 border-t border-navy-100 bg-white">
            <div className="flex gap-2">
              <Textarea
                value={newChatMessage}
                onChange={(e) => setNewChatMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                placeholder="Escribe un mensaje..."
                className="resize-none text-sm"
                rows={2}
                data-testid="case-chat-input"
              />
              <Button
                onClick={sendChatMessage}
                disabled={!newChatMessage.trim() || sendingChat || !caseData?.user_id}
                className="bg-emerald-600 hover:bg-emerald-700 px-4"
                data-testid="case-chat-send-btn"
              >
                {sendingChat ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Admin Actions */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* DS-160 Management */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-elevated p-6"
          >
            <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
              Gestión DS-160
            </h3>
            <div className="space-y-4">
              {/* Botón para ver respuestas del cuestionario */}
              <Button
                onClick={() => navigate(`/admin/cases/${caseId}/ds160-review`)}
                variant="outline"
                className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="view-ds160-answers-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver Respuestas del Cuestionario
              </Button>
              
              <div className="border-t border-navy-100 pt-4">
                <Label>Número de Confirmación</Label>
                <Input
                  value={ds160Data.confirmation_number}
                  onChange={(e) => setDs160Data({ ...ds160Data, confirmation_number: e.target.value })}
                  placeholder="AA0123456789"
                  data-testid="ds160-confirmation-input"
                />
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="checkbox"
                  id="ds160-completed"
                  checked={ds160Data.completed}
                  onChange={(e) => setDs160Data({ ...ds160Data, completed: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="ds160-completed">Marcar como completado</Label>
              </div>
              <Button
                onClick={updateDS160}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="update-ds160-btn"
              >
                Actualizar DS-160
              </Button>
            </div>
          </motion.div>

          {/* Final Result */}
          {caseData.current_step >= 5 && !caseData.final_result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="card-elevated p-6"
            >
              <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
                Resultado Final
              </h3>
              <p className="text-navy-600 mb-4">
                Selecciona el resultado de la entrevista consular:
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => setFinalResult("approved")}
                  disabled={settingResult}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="set-approved-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Visa Aprobada
                </Button>
                <Button
                  onClick={() => setFinalResult("denied")}
                  disabled={settingResult}
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="set-denied-btn"
                >
                  Visa Negada
                </Button>
                <Button
                  onClick={() => setFinalResult("administrative_processing")}
                  disabled={settingResult}
                  variant="outline"
                  className="w-full border-amber-300 text-amber-600 hover:bg-amber-50"
                  data-testid="set-processing-btn"
                >
                  Procesamiento Administrativo
                </Button>
              </div>
            </motion.div>
          )}

          {/* Current Result */}
          {caseData.final_result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`card-elevated p-6 border-l-4 ${
                caseData.final_result === "approved" ? "border-emerald-500" :
                caseData.final_result === "denied" ? "border-red-500" : "border-amber-500"
              }`}
            >
              <h3 className="font-heading text-lg font-bold text-navy-900 mb-2">
                Resultado Registrado
              </h3>
              <p className={`text-2xl font-bold ${
                caseData.final_result === "approved" ? "text-emerald-600" :
                caseData.final_result === "denied" ? "text-red-600" : "text-amber-600"
              }`}>
                {caseData.final_result === "approved" ? "APROBADA" :
                 caseData.final_result === "denied" ? "NEGADA" : "PROCESAMIENTO ADMINISTRATIVO"}
              </p>
            </motion.div>
          )}

          {/* Internal Notes Section */}
          <CaseNotesSection caseId={caseId} className="mt-6" />
        </div>
      </div>

      {/* Modal para Agendar Cita */}
      <Dialog open={showMeetingModal} onOpenChange={setShowMeetingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-blue-600" />
              Agendar Cita con Cliente
            </DialogTitle>
            <DialogDescription>
              Programa una reunión con {caseData?.client_name || "el cliente"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-4">
            <div>
              <Label>Fecha *</Label>
              <Input
                type="date"
                value={meetingForm.date}
                onChange={(e) => setMeetingForm({...meetingForm, date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Hora *</Label>
              <Select 
                value={meetingForm.time} 
                onValueChange={(value) => setMeetingForm({...meetingForm, time: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona hora" />
                </SelectTrigger>
                <SelectContent>
                  {["09:00", "10:00", "11:00", "12:00", "15:00", "16:00", "17:00"].map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Cita</Label>
              <Select 
                value={meetingForm.appointment_type} 
                onValueChange={(value) => setMeetingForm({...meetingForm, appointment_type: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="videollamada">📹 Videollamada</SelectItem>
                  <SelectItem value="llamada">📞 Llamada telefónica</SelectItem>
                  <SelectItem value="chat">💬 Chat</SelectItem>
                  <SelectItem value="presencial">👤 Presencial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Asunto/Motivo *</Label>
              <Input
                value={meetingForm.subject}
                onChange={(e) => setMeetingForm({...meetingForm, subject: e.target.value})}
                placeholder="Ej: Revisión de documentos"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Link de Zoom/Meet (opcional)</Label>
              <Input
                value={meetingForm.zoom_link}
                onChange={(e) => setMeetingForm({...meetingForm, zoom_link: e.target.value})}
                placeholder="https://zoom.us/j/..."
                className="mt-1"
              />
              <p className="text-xs text-navy-400 mt-1">
                Si no agregas link, se usará el enlace de Acuity predeterminado
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMeetingModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={createMeeting}
              disabled={creatingMeeting || !meetingForm.date || !meetingForm.time || !meetingForm.subject}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creatingMeeting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarPlus className="w-4 h-4 mr-2" />
              )}
              Agendar Cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminCaseDetail;
